import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getCanAccessPath } from './App.util';
import { RoutePaths, SIDE_MENU_EVENT } from './constants';
import { LocalStorageKey } from './constants/localStorageKey';
import { Store } from './global-state/types';
import { useLocalStorage, useStoreInformation, useStores, useUser } from './hooks';
import { PermissionItemsMap, permissionTypes, UserPermissionType } from './hooks/useUserPermissions';
import { Home, NotFound } from './pages';
import { YearlyStockTakePage } from './pages/yearlyStockTake/YearlyStockTakePage';
import { Icons } from './stories/atoms';
import { Animation, Animations, Banner } from './stories/molecules';
import { ActionButton, BannerItemsType } from './stories/molecules/banner';
import { Header, SideMenu } from './stories/organisms';
import { muiTheme } from './theme';
import { commonTranslations, errors, homeTranslations } from './translations';
import { sideMenuTranslations } from './translations/navigation';
import { getUniqueId, getUserPermissions, isFailureResponse } from './utils';
import useEventTracker from './hooks/useEventTracker';
import { AnimationWrapper, AppContainer } from './App.styles';
import { RouteList } from './App';

const widgetsForNonAuthorizedUsers: UserPermissionType[] = [
  'shareOfReceiptWidget',
  'topSellingGarmentsWidget',
  'salesPerformanceWidget',
  'conversionRateWidget',
  'storeInventoryCount',
];

const createPermissionItemsMap = (types: UserPermissionType[]): PermissionItemsMap => {
  return types.reduce((acc, permissionType) => {
    if (widgetsForNonAuthorizedUsers.includes(permissionType)) {
      acc[permissionType] = 'R';
    } else {
      acc[permissionType] = '';
    }
    return acc;
  }, {} as PermissionItemsMap);
};

const nonAuthorizedPermissions = createPermissionItemsMap(permissionTypes as unknown as UserPermissionType[]);

const { home, dashboards, yearlyStockTake } = sideMenuTranslations;

const RouteConfigMap = {
  [RoutePaths.HOME]: {
    path: RoutePaths.HOME,
    translation: home,
    element: <Home />,
    icon: Icons.HOME,
  },
  [RoutePaths.YEARLYSTOCKTAKE]: {
    path: RoutePaths.YEARLYSTOCKTAKE,
    translation: yearlyStockTake,
    element: <YearlyStockTakePage />,
    icon: Icons.DROPBOX,
  },
};

export const AppNonSignin: FC<PropsWithChildren<unknown>> = observer(() => {
  const trackEvent = useEventTracker();
  const [isError, setIsError] = useState(false);
  const [isFetchingStore, setIsFetchingStore] = useState<boolean>(true);
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);
  const [systemBanners, setSystemBanners] = useState<BannerItemsType[]>([]);
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  const {
    set,
    get: { keyToken, profile, userPermissions },
  } = useUser();
  const { fetchStoresData } = useStores();
  const { getItem, setItem } = useLocalStorage();
  const { t, i18n } = useTranslation();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('desktopLarge'));
  const { exclusionListCount } = useStoreInformation();
  const storeIdParam = new URLSearchParams(window.location.search).get('storeId') as string;
  const mobileDeviceKey = new URLSearchParams(window.location.search).get('key') as string;

  useEffect(() => {
    if (!i18n.resolvedLanguage) {
      setSystemBanners(() => [
        {
          id: getUniqueId(),
          label: t(
            homeTranslations.banners.translationError.key,
            homeTranslations.banners.translationError.defaultValue,
          ),
          type: 'warning',
          actionButton: ActionButton.Close,
          hasNoMargin: true,
        },
      ]);
      return;
    }
  }, [i18n.resolvedLanguage]);

  useEffect(() => {
    const getToken = async () => {
      set({ keyToken: mobileDeviceKey });
    };
    getToken();
  }, [mobileDeviceKey]);

  useEffect(() => {
    if (!keyToken) return;
    const getStoreData = async () => {
      const storesResponse = await fetchStoresData(storeIdParam);
      if (isFailureResponse(storesResponse)) {
        setIsError(true); // This might not be a core error
        return;
      }

      const { data } = storesResponse;

      // Setting localStorage using url storeId param
      setItem(LocalStorageKey.SelectedStore, storeIdParam);

      set({
        userPermissions: getUserPermissions(nonAuthorizedPermissions),
        stores: [data] as Store[],
        currentStoreId: storeIdParam,
      });
      setIsFetchingStore(false);
    };

    getStoreData();
  }, [keyToken]);

  const featureMapping = new Map<string, boolean>();

  const filteredMenuList = () => {
    const routes: RouteList[] = [
      {
        section: dashboards,
        links: [RouteConfigMap[RoutePaths.HOME], RouteConfigMap[RoutePaths.YEARLYSTOCKTAKE]],
      },
    ];

    const filteredList = routes.map((section) => {
      const formatLinks = section.links.filter((link) => {
        if (featureMapping.has(link.path)) {
          const isEnabled = featureMapping.get(link.path);

          if (!isEnabled) return false;
        }
        return getCanAccessPath(link.path, userPermissions, true, exclusionListCount);
      });

      return {
        ...section,
        links: formatLinks,
      };
    });
    return filteredList;
  };

  const nonSignInPath = `?storeId=${storeIdParam}&key=${mobileDeviceKey}`;

  return (
    <>
      {isError && (
        <AnimationWrapper>
          <Animation
            title={t(errors.pageNotFound.title.key, errors.pageNotFound.title.defaultValue)}
            subtitle={t(errors.pageNotFound.subtitle.key, errors.pageNotFound.subtitle.defaultValue)}
            animation={Animations.SAD}
          />
        </AnimationWrapper>
      )}
      {isFetchingStore && (
        <AnimationWrapper>
          <Animation
            title={t(commonTranslations.loading.key, commonTranslations.loading.defaultValue)}
            animation={Animations.LOADING}
          />
        </AnimationWrapper>
      )}

      {!isError && !isFetchingStore && (
        <>
          <Banner
            isSticky
            items={systemBanners}
            onChange={(items) => {
              setSystemBanners(items);
            }}
          />
          <AppContainer isFullScreen={isDesktop}>
            <Banner
              items={bannerItems}
              onChange={(items) => {
                setBannerItems(items);
              }}
            />

            <Header
              isNonSignPage={true}
              onOpenNav={() => {
                setIsSliderOpen(true);
                trackEvent(SIDE_MENU_EVENT, 'click', [{ name: 'isMenuOpen', stringValue: 'true' }]);
              }}
              currentPathNameTranslation={home}
            />

            <SideMenu
              isNonSignPage={true}
              header={{
                profile,
                storeId: storeIdParam,
              }}
              list={filteredMenuList()}
              nonSignInPath={nonSignInPath}
              isOpen={isSliderOpen}
              onClose={() => {
                setIsSliderOpen(false);
                trackEvent(SIDE_MENU_EVENT, 'click', [{ name: 'isMenuOpen', stringValue: 'false' }]);
              }}
            />
            <Routes>
              {filteredMenuList().map(({ links }) =>
                links.map((link) => <Route key={link.path} path={link.path} element={link.element} />),
              )}
              <Route index element={<Navigate to="/home" />} />
              <Route path={`/home/${getItem(LocalStorageKey.SelectedStore)}`} element={<Navigate to="/home" />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppContainer>
        </>
      )}
    </>
  );
});
